import type { Result } from '../core/result';
import { ok, err } from '../core/result';
import type { FieldColourMapFile, FieldColourMapGrid } from './generated/fieldColourMap';
import { createAjvFactory } from './validate';
import schema from './schema/fieldColourMap.schema.json';
import { decodeBase64 } from '../core/base64';

export { decodeBase64 };

export type { FieldColourMapFile, FieldColourMapGrid };

/** Painted colour on a ground grid, cells row by row from the south-west. */
export interface FieldColourLayer {
  readonly grid: FieldColourMapGrid;
  /** Three sRGB bytes a cell. */
  readonly albedo: Uint8Array;
  /** One byte a cell, 0 to 255 for none to whole. */
  readonly weight: Uint8Array;
}

/** The painted field colour: the polders' fine layer, and the far layer past the layout bounds. */
export interface FieldColourMap extends FieldColourLayer {
  readonly far: FieldColourLayer;
}

let validate: ReturnType<ReturnType<typeof createAjvFactory>['compile']> | null = null;

function schemaErrors(value: unknown): readonly string[] {
  validate ??= createAjvFactory().compile(schema);
  if (validate(value)) return [];
  return (validate.errors ?? []).map((e) => `${e.instancePath || '/'}: ${e.message ?? 'invalid'}`);
}

function sizeError(name: string, bytes: Uint8Array, grid: FieldColourMapGrid, perCell: number) {
  const needed = grid.nx * grid.ny * perCell;
  return bytes.length === needed
    ? []
    : [
        `${name} holds ${String(bytes.length)} bytes where the ${String(grid.nx)} x ${String(grid.ny)} grid needs ${String(needed)}`,
      ];
}

interface EncodedLayer {
  readonly grid: FieldColourMapGrid;
  readonly albedo: string;
  readonly weight: string;
}

function decodeLayer(
  layer: EncodedLayer,
  prefix: string
): { readonly layer: FieldColourLayer; readonly errors: readonly string[] } {
  const albedo = decodeBase64(layer.albedo);
  const weight = decodeBase64(layer.weight);
  return {
    layer: { grid: layer.grid, albedo, weight },
    errors: [
      ...sizeError(`${prefix}albedo`, albedo, layer.grid, 3),
      ...sizeError(`${prefix}weight`, weight, layer.grid, 1),
    ],
  };
}

export function parseFieldColourMap(value: unknown): Result<FieldColourMap, readonly string[]> {
  const errors = schemaErrors(value);
  if (errors.length > 0) return err(errors);
  const file = value as FieldColourMapFile;
  const near = decodeLayer(file, '');
  const far = decodeLayer(file.far, 'far ');
  const sizes = [...near.errors, ...far.errors];
  return sizes.length > 0 ? err(sizes) : ok({ ...near.layer, far: far.layer });
}

export function fieldColourMapFrom(value: unknown): FieldColourMap {
  const parsed = parseFieldColourMap(value);
  if (!parsed.ok) throw new Error(`fieldColourMap.json is invalid: ${parsed.error.join('; ')}`);
  return parsed.value;
}
